/**
 * OPS — Cliente Detalhe
 * Perfil, plano, uso, módulos, faturas e usuários vinculados
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Building2, RefreshCw, Users, Video, Cpu, HardDrive,
  Shield, CreditCard, ChevronRight, CheckCircle2, XCircle, Clock,
  TrendingUp, Package, AlertTriangle, Crown
} from 'lucide-react';
import { AZURE } from '../../services/api';
import { COLORS } from '../../components/governance/governanceTheme';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const CLIENT_LABELS = {
  pt: {
    back: '← Voltar para Clientes',
    usage: 'Uso Atual',
    athletes: 'Atletas',
    staff: 'Staff',
    videos: 'Vídeos',
    aiQueries: 'Queries AI',
    storage: 'armazenamento',
    info: 'Informações',
    type: 'Tipo',
    sport: 'Esporte',
    city: 'Cidade',
    country: 'País',
    onboarding: 'Onboarding',
    progress: 'Progresso',
    createdAt: 'Criado em',
    modules: 'Módulos',
    noModules: 'Nenhum módulo ativado',
    users: 'Usuários',
    noUsers: 'Sem usuários vinculados',
    invoices: 'Faturas',
    noInvoices: 'Sem faturas',
    clientSince: 'Cliente desde',
  },
  en: {
    back: '← Back to Clients',
    usage: 'Current Usage',
    athletes: 'Athletes',
    staff: 'Staff',
    videos: 'Videos',
    aiQueries: 'AI Queries',
    storage: 'storage',
    info: 'Info',
    type: 'Type',
    sport: 'Sport',
    city: 'City',
    country: 'Country',
    onboarding: 'Onboarding',
    progress: 'Progress',
    createdAt: 'Created at',
    modules: 'Modules',
    noModules: 'No active modules',
    users: 'Users',
    noUsers: 'No linked users',
    invoices: 'Invoices',
    noInvoices: 'No invoices',
    clientSince: 'Client since',
  },
};

const PLAN_META = {
  starter:    { label: 'Starter',    color: '#6b7280', bg: '#f3f4f6', icon: Package },
  pro:        { label: 'Pro',        color: '#b48c2c', bg: '#fef9ee', icon: TrendingUp },
  enterprise: { label: 'Enterprise', color: '#111111', bg: '#e5e7eb', icon: Crown },
  custom:     { label: 'Custom',     color: '#7c3aed', bg: '#ede9fe', icon: Shield },
};

const STATUS_COLORS = {
  active: '#059669', trialing: '#d97706', past_due: '#dc2626',
  canceled: '#6b7280', suspended: '#dc2626',
  paid: '#059669', pending: '#d97706', overdue: '#dc2626',
};

const InfoRow = ({ label, value, mono }) => (
  <div className="flex justify-between py-2 border-b border-gray-50 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className={`text-sm font-semibold text-gray-900 ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</span>
  </div>
);

// UI_PADRAO: barra de cota é informação, não emergência — preenchimento
// neutro único; estouro sinalizado por peso de fonte, nunca vermelho/âmbar
// (vermelho é reservado a alerta clínico e a status financeiro real).
const UsageBar = ({ used, limit, label }) => {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const overLimit = used > limit && limit > 0;
  // UI_PADRAO: barra sempre neutra. Estouro de cota é informação (destaque por
  // peso de fonte), não alerta clínico — sem vermelho/âmbar/verde decorativo.
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-500">{label}</span>
        <span className={`font-mono ${overLimit ? 'font-bold text-gray-900' : 'font-semibold text-gray-700'}`}>
          {used ?? 0}{limit ? ` / ${limit}` : ''}
        </span>
      </div>
      {limit > 0 && (
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-300 bg-gray-700"
               style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
};

const GovernanceClientDetailPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { lang } = useGovernanceLang();
  const t = CLIENT_LABELS[lang] || CLIENT_LABELS.pt;

  const fetchDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp } = await AZURE.get(`/governance/clients/${id}`);
      setData(resp.data || resp);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDetail(); }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: COLORS.primary }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-700 font-semibold">{error}</p>
        <Link to="/clientes" className="text-sm text-gray-500 mt-2 inline-block">← Voltar</Link>
      </div>
    );
  }

  const p = data?.profile || {};
  const modules = data?.modules || [];
  const invoices = data?.invoices || [];
  const usageHistory = data?.usage_history || [];
  const linkedUsers = data?.linked_users || [];

  const planMeta = PLAN_META[p.plan_key] || PLAN_META.starter;
  const PlanIcon = planMeta.icon;

  return (
    <div>
      {/* Back + Header */}
      <div className="flex items-center gap-3 mb-4">
        <img src="/vukz-logo.png" alt="Vukz" className="w-9 h-9 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
        <Link to="/clientes" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" />
          {t.back.replace('← ', '')}
        </Link>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        {/* Banner */}
        <div className="h-20 bg-gradient-to-r from-gray-100 to-gray-200" />
        <div className="px-6 pb-6 -mt-8">
          <div className="flex items-end gap-4 mb-4">
            <div className="w-16 h-16 rounded-xl bg-white border-4 border-white shadow-md flex items-center justify-center text-xl font-bold text-gray-400 overflow-hidden">
              {p.logo_url ? (
                <img src={p.logo_url} alt="" className="w-full h-full object-cover"
                     onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                (p.institution_name || '?').substring(0, 2).toUpperCase()
              )}
            </div>
            <div className="flex-1 pt-8">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-extrabold text-gray-900">
                  {p.trade_name || p.institution_name}
                </h1>
                {p.verified && (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                )}
              </div>
              <p className="text-sm text-gray-500">
                {[p.org_type || p.institution_type, p.city, p.country].filter(Boolean).join(' · ')}
                {p.primary_sport && ` · ${p.primary_sport}`}
              </p>
            </div>
            <button onClick={fetchDetail} className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Plan + Status row */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: planMeta.bg }}>
              <PlanIcon className="w-4 h-4" style={{ color: planMeta.color }} />
              <span className="text-sm font-bold" style={{ color: planMeta.color }}>{planMeta.label}</span>
            </div>
            {p.subscription_status && (
              <span className="flex items-center gap-1.5 text-sm font-medium">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[p.subscription_status] }} />
                {p.subscription_status}
              </span>
            )}
            {p.monthly_price_cents > 0 && (
              <span className="text-sm font-mono font-bold" style={{ color: COLORS.gold }}>
                R$ {(p.monthly_price_cents / 100).toLocaleString('pt-BR')}/mês
              </span>
            )}
            {p.subscription_started && (
              <span className="text-xs text-gray-400">
                {t.clientSince} {new Date(p.subscription_started).toLocaleDateString('pt-BR')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Grid: Usage + Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Usage */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> {t.usage}
          </h3>
          <UsageBar used={p.active_athletes ?? p.athletes_count ?? 0} limit={p.athlete_limit} label={t.athletes} />
          <UsageBar used={p.active_staff ?? p.staff_count ?? 0} limit={p.staff_limit} label={t.staff} />
          <UsageBar used={p.videos_processed ?? 0} limit={p.video_credits_monthly} label={t.videos} />
          <UsageBar used={p.ai_queries ?? 0} limit={p.ai_credits_monthly} label={t.aiQueries} />
          {p.storage_mb != null && (
            <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
              <HardDrive className="w-3 h-3" /> {p.storage_mb} MB {t.storage}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4" /> {t.info}
          </h3>
          <InfoRow label="ID" value={`#${p.institution_id}`} mono />
          <InfoRow label={t.type} value={p.org_type || p.institution_type} />
          <InfoRow label={t.sport} value={p.primary_sport} />
          <InfoRow label={t.city} value={p.city} />
          <InfoRow label={t.country} value={p.country} />
          <InfoRow label={t.onboarding} value={p.setup_status} />
          <InfoRow label={t.progress} value={p.progress_pct != null ? `${p.progress_pct}%` : null} />
          <InfoRow label={t.createdAt} value={p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : null} />
        </div>

        {/* Modules */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-4 h-4" /> {t.modules} ({modules.length})
          </h3>
          {modules.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">{t.noModules}</p>
          ) : (
            <div className="space-y-2">
              {modules.map((m, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2">
                    {m.is_active ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-300" />
                    )}
                    <span className="text-sm font-medium text-gray-900">{m.module_name || m.module_key}</span>
                  </div>
                  {m.monthly_cost_cents > 0 && (
                    <span className="text-xs font-mono text-gray-400">
                      R$ {(m.monthly_cost_cents / 100).toFixed(0)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Users + Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Linked Users */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" /> {t.users} ({linkedUsers.length})
          </h3>
          {linkedUsers.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">{t.noUsers}</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {linkedUsers.map((u, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{u.name || u.email}</div>
                    <div className="text-xs text-gray-400">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.org_role && (
                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-gray-100 text-gray-600">
                        {u.org_role}
                      </span>
                    )}
                    <span className="px-2 py-0.5 rounded text-xs bg-gray-50 text-gray-400">{u.role}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Invoices */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> {t.invoices} ({invoices.length})
          </h3>
          {invoices.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">{t.noInvoices}</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {invoices.map((inv, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      R$ {((inv.amount_cents || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs text-gray-400">
                      {inv.issued_at ? new Date(inv.issued_at).toLocaleDateString('pt-BR') : ''}
                      {inv.description ? ` · ${inv.description}` : ''}
                    </div>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-bold"
                        style={{ color: STATUS_COLORS[inv.status] || '#6b7280' }}>
                    {inv.status === 'paid' && <CheckCircle2 className="w-3.5 h-3.5" />}
                    {inv.status === 'pending' && <Clock className="w-3.5 h-3.5" />}
                    {inv.status === 'overdue' && <AlertTriangle className="w-3.5 h-3.5" />}
                    {inv.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GovernanceClientDetailPage;
